// src/pages/GoingGammaLeadMagnetEs.js

import React, { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { 
  SparklesIcon, 
  CheckCircleIcon, 
  PlayCircleIcon, 
  EyeIcon, 
  BoltIcon, 
  KeyIcon, 
  ChatBubbleOvalLeftEllipsisIcon,
  SpeakerWaveIcon 
} from '@heroicons/react/24/solid';
import { useLocation } from 'react-router-dom';

// --- REAL IMPORTS FROM YOUR PROJECT ---
import { sendCodedLog } from '../../services/logService';
import Common from '../../common/Common';
import UrlConfig from '../../data/UrlConfig';

const API_URLS = UrlConfig;

const GoingGammaLeadMagnetEs = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState(localStorage.getItem('track_user_email') || '');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const location = useLocation();

  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    sendCodedLog({
      message: 'Clic en el botón "Sí, Envíame Mi Kit Completo!"',
      event: 'gamma_kit_submit_es',
      ...Common.getCommonLogData(location)
    });

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Por favor, ingresa una dirección de correo electrónico válida.');
      setIsLoading(false);
      sendCodedLog({
        message: 'Error de validación frontend: correo electrónico inválido',
        event: 'gamma_kit_validation_error_es',
        data: { errorType: 'invalid_email' },
        ...Common.getCommonLogData(location)
      });
      return;
    }

    localStorage.setItem('track_user_email', email.trim());
    name && localStorage.setItem('track_user_name', name.trim());

    try {
      const response = await fetch(API_URLS.leads_add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim(),
          folder: 'gamma-activation-kit',
          page: location.pathname,
          lang: 'es',
          newsletter: ['gamma-activation-kit'],
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
        sendCodedLog({
          message: 'Formulario del Kit Gamma enviado exitosamente',
          event: 'gamma_kit_success_es',
          ...Common.getCommonLogData(location)
        });
      } else {
        throw new Error(data.message || 'Ocurrió un error al procesar tu solicitud.');
      }
    } catch (err) {
      setError(err.message);
      sendCodedLog({
        message: `Error al enviar el formulario del Kit Gamma: ${err.message}`,
        event: 'gamma_kit_backend_error_es',
        data: { errorMessage: err.message },
        ...Common.getCommonLogData(location)
      });
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="relative bg-black text-white min-h-screen font-sans flex flex-col">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-black to-gray-900 opacity-95 z-0"></div>
      
      <main ref={ref} className={`relative z-10 flex-grow container mx-auto px-4 py-12 flex flex-col items-center ${inView ? "fade-in-animation" : "opacity-0"}`}>
        
        <h1 className="text-4xl sm:text-5xl font-bold text-center mb-4 leading-tight">
          Desbloquea Tu Mente Superior con el Estado Gamma
        </h1>
        <p className="text-lg sm:text-xl text-gray-400 text-center max-w-3xl mb-12">
          Mira a Bashar explicar la poderosa técnica de respiración, experimenta una meditación de activación y luego obtén tu kit gratuito para ir aún más profundo.
        </p>

        <div className="w-full max-w-4xl mx-auto mb-16">
            <div className="aspect-video w-full rounded-lg overflow-hidden shadow-2xl shadow-sky-500/20 border border-sky-700">
                <iframe 
                    src="https://www.youtube.com/embed/sAuC12thi_k" 
                    title="Técnica de Respiración del Estado Gamma de Bashar" 
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    allowFullScreen
                    className="w-full h-full"
                ></iframe>
            </div>
        </div>

        <div className="text-center w-full max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-8">¿Qué es el Estado Gamma y Por Qué Es Importante?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-black/20 p-6 rounded-lg border border-gray-800 backdrop-blur-sm"><BoltIcon className="w-10 h-10 mx-auto mb-4 text-sky-400"/><h3 className="text-xl font-semibold mb-2">Percepción Máxima</h3><p className="text-gray-400">Es la frecuencia de ondas cerebrales de procesamiento de información de alto nivel, creatividad y enfoque intenso.</p></div>
                <div className="bg-black/20 p-6 rounded-lg border border-gray-800 backdrop-blur-sm"><KeyIcon className="w-10 h-10 mx-auto mb-4 text-sky-400"/><h3 className="text-xl font-semibold mb-2">Desbloquea Tu Potencial</h3><p className="text-gray-400">Acceder a este estado te permite eludir la "Mente Física" crítica para conectar con tu "Mente Superior".</p></div>
                <div className="bg-black/20 p-6 rounded-lg border border-gray-800 backdrop-blur-sm"><ChatBubbleOvalLeftEllipsisIcon className="w-10 h-10 mx-auto mb-4 text-sky-400"/><h3 className="text-xl font-semibold mb-2">Comunicación Más Clara</h3><p className="text-gray-400">Este es el estado donde la intuición, la sincronicidad y la inspiración se convierten en tu lenguaje principal.</p></div>
            </div>
        </div>

        <div className="text-center w-full max-w-4xl mx-auto mb-16">
          <h2 className="text-3xl font-bold mb-4">Experimenta un Adelanto del Estado Gamma Ahora</h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-6">
            Relaja tu mente y concéntrate en el centro de esta meditación fractal de 7 minutos para comenzar a activar tu propio estado Gamma.
          </p>
          
          <div className="flex items-center justify-center gap-2 text-sky-400 mb-6">
            <SpeakerWaveIcon className="w-6 h-6"/>
            <span className="font-semibold">Para una experiencia totalmente inmersiva, por favor usa auriculares.</span>
          </div>

          <div className="aspect-video w-full rounded-lg overflow-hidden shadow-2xl shadow-purple-500/20 border border-purple-700">
              <video 
                  src="https://gusaguiar.com/assets/fractal/Fratal - Gamma - 7m.mp4"
                  className="w-full h-full"
                  controls
                  loop
                  poster="https://gusaguiar.com/assets/fractal/Fratal - Gamma - 7m.jpg"
              >
                  Tu navegador no soporta la etiqueta de video.
              </video>
          </div>
        </div>

        <div className="w-full max-w-4xl flex flex-col md:flex-row gap-8 items-center bg-gray-900/50 border border-purple-700/50 rounded-xl p-8 shadow-2xl shadow-purple-500/20 backdrop-blur-sm">
            <div className="w-full md:w-1/2">
                <h3 className="font-bold text-2xl mb-4 text-sky-400">¿Listo para ir más profundo? Obtén tu Kit Completo.</h3>
                <p className="mb-6 text-gray-300">Si disfrutaste esto, inscríbete a continuación para recibir el "Kit de Activación Gamma" completo en tu bandeja de entrada, totalmente gratis:</p>
                <ul className="space-y-4">
                    <li className="flex items-start gap-3"><PlayCircleIcon className="w-6 h-6 text-purple-400 mt-1 flex-shrink-0" /><span><strong>La Meditación Fractal Completa de 15 Minutos:</strong> Obtén la versión completa e ininterrumpida de la meditación que acabas de ver.</span></li>
                    <li className="flex items-start gap-3"><SparklesIcon className="w-6 h-6 text-sky-400 mt-1 flex-shrink-0" /><span><strong>Meditación Gamma Holotrópica de 15 Minutos con Bashar:</strong> Sumérgete en un estado de conciencia más profundo.</span></li>
                    <li className="flex items-start gap-3"><EyeIcon className="w-6 h-6 text-pink-400 mt-1 flex-shrink-0" /><span><strong>Meditación Caleidoscópica de 15 Minutos:</strong> La meditación visual inmersiva del Dr. Joe Dispenza.</span></li>
                </ul>
            </div>
            
            <div className="w-full md:w-1/2 bg-black/30 border border-gray-700 rounded-xl p-6 sm:p-8">
              {submitted ? (
                <div className="text-center text-green-400 p-4 h-full flex flex-col justify-center items-center">
                  <CheckCircleIcon className="w-16 h-16 mx-auto mb-4" />
                  <p className="text-2xl font-semibold text-white">¡Éxito!</p>
                  <p className="text-lg">Tu kit completo está en camino a tu bandeja de entrada. ¡Disfruta el viaje!</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col space-y-5">
                  <h3 className="font-semibold text-lg text-center">Obtén Tu Kit Gratuito Ahora</h3>
                  <div>
                    <label htmlFor="name" className="block text-gray-300 text-sm font-medium mb-2">Tu Nombre (Opcional)</label>
                    <input type="text" id="name" value={name} onChange={(e) => setName(e.target.value)} className="w-full p-3 rounded-md bg-gray-900/70 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-sky-400" placeholder="Para que podamos saludarte apropiadamente"/>
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-gray-300 text-sm font-medium mb-2">Tu Mejor Email</label>
                    <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-3 rounded-md bg-gray-900/70 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-sky-400" placeholder="Donde enviaremos tu kit" required/>
                  </div>
                  {error && <p className="text-sm text-center text-red-400">{error}</p>}
                  <button type="submit" className="w-full text-lg py-3 px-4 rounded-xl bg-gradient-to-r from-sky-500 to-purple-600 font-semibold hover:from-sky-600 hover:to-purple-700 transition-all duration-300 disabled:opacity-50" disabled={isLoading}>
                    {isLoading ? 'Enviando...' : 'Sí, Envíame Mi Kit Completo!'}
                  </button>
                </form>
              )}
            </div>
        </div>
      </main>

      <footer className="relative z-10 text-center py-6 text-xs text-gray-600">
        © {new Date().getFullYear()} Gus • Aguiar | En una misión para elevar y transformar.
      </footer>
    </div>
  );
};

export default GoingGammaLeadMagnetEs;